import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { getCustomerDisplayName } from '@/utils/customerDisplayHelpers';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import {
  FileText,
  User,
  Receipt,
  CreditCard,
  RefreshCcw,
  XCircle,
  Clock,
  DollarSign,
  Eye,
} from 'lucide-react';
import { useNotify } from '@/hooks/useNotify';
import { useAuth } from '@/contexts/AuthContext';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import DetailPageHeader from '@/components/layout/DetailPageHeader';
import CreditNotePreviewModal from '@/components/credits/CreditNotePreviewModal';
import ApplyCreditsToInvoiceModal from '@/components/customers/credits/ApplyCreditsToInvoiceModal';
import RefundCreditModal from '@/components/customers/credits/RefundCreditModal';
import CancelCreditModal from '@/components/customers/credits/CancelCreditModal';
import { formatCurrency } from '@/lib/utils';

interface CreditDetails {
  id: string;
  credit_amount: number;
  remaining_amount: number;
  source_type: string;
  source_invoice_id: string | null;
  description: string | null;
  internal_description: string | null;
  status: string;
  credit_note_number: string | null;
  credit_note_issued_at: string | null;
  credit_note_issued_by: string | null;
  created_at: string;
  created_by: string;
  cancelled_at: string | null;
  cancelled_reason: string | null;
  customer: {
    id: string;
    name: string;
    last_name: string;
    customer_type: string;
    business_name?: string | null;
    street_address?: string | null;
    suburb?: string | null;
    city?: string | null;
    province_state?: string | null;
    postal_code?: string | null;
    emails?: string[];
    cell_numbers?: string[];
  } | null;
  source_invoice: {
    id: string;
    invoice_number: string | null;
  } | null;
}

const CreditDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { profile } = useAuth();
  const companyId = profile?.company_id;

  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Fetch credit details
  const { data: credit, isLoading, error } = useQuery({
    queryKey: ['credit-details', id],
    queryFn: async () => {
      if (!id) throw new Error('Credit ID is required');

      const { data, error } = await supabase
        .from('customer_credits')
        .select(`
          *,
          customer:customers!customer_credits_customer_id_fkey(
            id, name, last_name, customer_type, business_name,
            street_address, suburb, city, province_state, postal_code,
            emails, cell_numbers
          ),
          source_invoice:invoices!customer_credits_source_invoice_id_fkey(id, invoice_number)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as CreditDetails;
    },
    enabled: !!id,
  });

  // Fetch credit applications (invoices that used this credit)
  const { data: creditApplications } = useQuery({
    queryKey: ['credit-applications', id],
    queryFn: async () => {
      if (!id) return [];

      const { data, error } = await supabase
        .from('credit_applications')
        .select(`
          id,
          amount_applied,
          applied_at,
          application_status,
          invoice:invoices!credit_applications_invoice_id_fkey(
            id,
            invoice_number,
            total_amount,
            status
          )
        `)
        .eq('credit_id', id)
        .order('applied_at', { ascending: false });

      if (error) throw error;
      return data as Array<{
        id: string;
        amount_applied: number;
        applied_at: string | null;
        application_status: string | null;
        invoice: {
          id: string;
          invoice_number: string | null;
          total_amount: number | null;
          status: string;
        } | null;
      }>;
    },
    enabled: !!id,
  });

  // Mark as refunded mutation
  const markRefundedMutation = useMutation({
    mutationFn: async (data: { refundMethod: string; notes?: string }) => {
      if (!id) throw new Error('Credit ID is required');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: refundError } = await supabase
        .from('credit_refunds')
        .insert({
          credit_id: id,
          refund_amount: credit?.remaining_amount || 0,
          refund_method: data.refundMethod,
          notes: data.notes,
          created_by: user.id,
        });

      if (refundError) throw refundError;

      const { error: updateError } = await supabase
        .from('customer_credits')
        .update({
          status: 'refunded',
          remaining_amount: 0,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;
    },
    onSuccess: () => {
      notify.success('Credit marked as refunded');
      queryClient.invalidateQueries({ queryKey: ['credit-details', id] });
      setShowRefundModal(false);
    },
    onError: (error) => {
      notify.error(error);
    },
  });

  // Cancel credit mutation
  const cancelCreditMutation = useMutation({
    mutationFn: async (reason: string) => {
      if (!id) throw new Error('Credit ID is required');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('customer_credits')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          cancelled_by: user.id,
          cancelled_reason: reason,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      notify.success('Credit cancelled');
      queryClient.invalidateQueries({ queryKey: ['credit-details', id] });
      setShowCancelModal(false);
    },
    onError: (error) => {
      notify.error(error);
    },
  });


  const getStatusConfig = () => {
    if (!credit?.status) return undefined;
    
    const statusMap: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" | "success" }> = {
      issued: { label: 'Issued', variant: 'success' },
      applied: { label: 'Applied', variant: 'secondary' },
      refunded: { label: 'Refunded', variant: 'outline' },
      cancelled: { label: 'Cancelled', variant: 'destructive' },
    };
    
    return statusMap[credit.status] || { label: credit.status, variant: 'outline' };
  };

  const getSourceLabel = (sourceType: string) => {
    switch (sourceType) {
      case 'overpayment':
        return 'Overpayment';
      case 'manual':
        return 'Manual Credit';
      case 'refund':
        return 'Refund';
      default:
        return sourceType;
    }
  };

  const getCustomerName = () => {
    if (!credit?.customer) return 'Unknown Customer';
    return getCustomerDisplayName(credit.customer);
  };

  if (isLoading) {
    return (
      <DetailPageLayout>
        <div className="space-y-4">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64" />
            <div className="lg:col-span-2">
              <Skeleton className="h-64" />
            </div>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !credit) {
    return (
      <DetailPageLayout>
        <div className="text-center py-12">
          <p className="text-destructive font-semibold mb-4">Credit not found or an error occurred.</p>
          <Button asChild variant="outline">
            <Link to="/finances/credit-notes">Back to Credit Notes</Link>
          </Button>
        </div>
      </DetailPageLayout>
    );
  }

  const isActive = credit.status === 'issued' && credit.remaining_amount > 0;
  const hasCreditNote = !!credit.credit_note_number;

  // Build actions based on credit state
  const secondaryActions = [];
  
  if (hasCreditNote) {
    secondaryActions.push({
      label: "Preview Credit Note",
      icon: Eye,
      onClick: () => setShowPreviewModal(true)
    });
  }
  
  if (isActive) {
    secondaryActions.push(
      {
        label: "Apply to Invoice",
        icon: CreditCard,
        onClick: () => setShowApplyModal(true)
      },
      {
        label: "Mark as Refunded",
        icon: RefreshCcw,
        onClick: () => setShowRefundModal(true)
      },
      {
        label: "Cancel Credit",
        icon: XCircle,
        onClick: () => setShowCancelModal(true),
        destructive: true,
        requiresConfirmation: {
          title: "Cancel Credit",
          description: "Are you sure you want to cancel this credit? This action cannot be undone."
        }
      }
    );
  }

  return (
    <DetailPageLayout>
      <DetailPageHeader
        title={credit.credit_note_number || 'Credit'}
        subtitle={`Created ${format(new Date(credit.created_at), 'dd MMMM yyyy')}`}
        status={getStatusConfig()}
        backLink={{ label: "Back to Credit Notes", href: "/finances/credit-notes" }}
        secondaryActions={secondaryActions}
      />

      {/* Main Grid - Sidebar LEFT, Content RIGHT */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT SIDEBAR - Related Entities */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Credit Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-muted-foreground">Credit Note Number</h3>
                <p className="font-mono text-lg font-semibold">
                  {credit.credit_note_number || 'Not Generated'}
                </p>
              </div>

              {/* Related Information Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">Related Information</h3>
                
                {/* Customer Relationship */}
                {credit.customer && (
                  <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-primary" />
                        <span className="text-sm font-medium">Customer</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        asChild
                      >
                        <Link to={`/customers?customer=${credit.customer?.id}`}>View Customer</Link>
                      </Button>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      <div className="font-medium text-foreground">{getCustomerName()}</div>
                      {credit.customer.emails && credit.customer.emails.length > 0 && (
                        <div>Email: {credit.customer.emails[0]}</div>
                      )}
                      {credit.customer.cell_numbers && credit.customer.cell_numbers.length > 0 && (
                        <div>Phone: {credit.customer.cell_numbers[0]}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Source Invoice */}
                {credit.source_invoice && (
                  <div className="p-3 bg-success/5 rounded-lg border border-success/20">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-success" />
                        <span className="text-sm font-medium">Source Invoice</span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/invoices/${credit.source_invoice?.id}`)}
                      >
                        View Invoice
                      </Button>
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      <div className="font-medium text-foreground">{credit.source_invoice.invoice_number || 'Invoice'}</div>
                      <div>Credit created from overpayment</div>
                    </div>
                  </div>
                )}

                {/* Applied Invoices - Invoices that used this credit */}
                {creditApplications && creditApplications.length > 0 && (
                  <div className="p-3 bg-warning/5 rounded-lg border border-warning/20">
                    <div className="flex items-center gap-2 mb-3">
                      <Receipt className="h-4 w-4 text-warning" />
                      <span className="text-sm font-medium">
                        Applied to {creditApplications.length} Invoice{creditApplications.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {creditApplications.map((application) => (
                        <div 
                          key={application.id}
                          className="flex items-center justify-between p-2 bg-background rounded border"
                        >
                          <div className="text-sm">
                            <div className="font-medium">
                              {application.invoice?.invoice_number || 'Invoice'}
                            </div>
                            <div className="text-muted-foreground">
                              {formatCurrency(application.amount_applied)} applied
                              {application.applied_at && (
                                <> on {format(new Date(application.applied_at), 'dd MMMM yyyy')}</>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => application.invoice && navigate(`/invoices/${application.invoice.id}`)}
                          >
                            View
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="p-3 bg-muted rounded-lg border">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Timeline</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                      <div>
                        <p className="font-medium">Created</p>
                        <p className="text-muted-foreground">{format(new Date(credit.created_at), 'dd MMMM yyyy')}</p>
                      </div>
                    </div>
                    
                    {credit.credit_note_issued_at && (
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5" />
                        <div>
                          <p className="font-medium">Credit Note Generated</p>
                          <p className="text-muted-foreground">{format(new Date(credit.credit_note_issued_at), 'dd MMMM yyyy')}</p>
                        </div>
                      </div>
                    )}
                    
                    {credit.cancelled_at && (
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 rounded-full bg-destructive mt-1.5" />
                        <div>
                          <p className="font-medium">Cancelled</p>
                          <p className="text-muted-foreground">{format(new Date(credit.cancelled_at), 'dd MMMM yyyy')}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RIGHT - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Financial Summary Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Financial Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="text-center p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Original Amount</p>
                  <p className="text-2xl font-bold">{formatCurrency(credit.credit_amount)}</p>
                </div>
                <div className="text-center p-4 bg-success/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Remaining Balance</p>
                  <p className="text-2xl font-bold text-success">{formatCurrency(credit.remaining_amount)}</p>
                </div>
                <div className="text-center p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">Used</p>
                  <p className="text-2xl font-bold text-primary">
                    {formatCurrency(credit.credit_amount - credit.remaining_amount)}
                  </p>
                </div>
              </div>

              <Separator className="my-6" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Source</p>
                  <p className="font-medium">{getSourceLabel(credit.source_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Credit Note Description</p>
                  <p className="font-medium">{credit.description || 'No description'}</p>
                </div>
              </div>
              {credit.internal_description && (
                <div className="mt-4 p-3 rounded-md bg-muted/50 border border-border">
                  <p className="text-sm text-muted-foreground mb-1">Internal Notes</p>
                  <p className="text-sm">{credit.internal_description}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cancellation Info */}
          {credit.status === 'cancelled' && credit.cancelled_reason && (
            <Card className="border-destructive">
              <CardHeader>
                <CardTitle className="text-destructive">Cancellation Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <p className="text-sm text-muted-foreground">Cancelled On</p>
                    <p className="font-medium">
                      {credit.cancelled_at 
                        ? format(new Date(credit.cancelled_at), 'dd MMMM yyyy') 
                        : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Reason</p>
                    <p className="font-medium">{credit.cancelled_reason}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Modals */}
      {hasCreditNote && (
        <CreditNotePreviewModal 
          creditId={credit.id} 
          isOpen={showPreviewModal}
          onOpenChange={setShowPreviewModal}
        />
      )}

      {credit?.customer && (
        <>
          <ApplyCreditsToInvoiceModal
            isOpen={showApplyModal}
            onClose={() => setShowApplyModal(false)}
            credit={{
              id: credit.id,
              customer_id: credit.customer.id,
              credit_amount: credit.credit_amount,
              remaining_amount: credit.remaining_amount,
              source_type: credit.source_type,
              source_invoice_id: credit.source_invoice_id,
              description: credit.description,
              status: credit.status as 'issued' | 'applied' | 'refunded' | 'cancelled',
              credit_note_number: credit.credit_note_number,
              credit_note_issued_at: credit.credit_note_issued_at,
              credit_note_issued_by: credit.credit_note_issued_by,
              cancelled_at: credit.cancelled_at,
              cancelled_reason: credit.cancelled_reason,
              cancelled_by: null,
              created_at: credit.created_at,
              created_by: credit.created_by,
              updated_at: credit.created_at,
              source_invoice: credit.source_invoice,
            }}
            customerId={credit.customer.id}
            onApply={() => {
              queryClient.invalidateQueries({ queryKey: ['credit-details', id] });
            }}
            isLoading={false}
          />

          <RefundCreditModal
            isOpen={showRefundModal}
            onClose={() => setShowRefundModal(false)}
            credit={{
              id: credit.id,
              customer_id: credit.customer.id,
              credit_amount: credit.credit_amount,
              remaining_amount: credit.remaining_amount,
              source_type: credit.source_type,
              source_invoice_id: credit.source_invoice_id,
              description: credit.description,
              status: credit.status as 'issued' | 'applied' | 'refunded' | 'cancelled',
              credit_note_number: credit.credit_note_number,
              credit_note_issued_at: credit.credit_note_issued_at,
              credit_note_issued_by: credit.credit_note_issued_by,
              cancelled_at: credit.cancelled_at,
              cancelled_reason: credit.cancelled_reason,
              cancelled_by: null,
              created_at: credit.created_at,
              created_by: credit.created_by,
              updated_at: credit.created_at,
              source_invoice: credit.source_invoice,
            }}
            onRefund={(data) => markRefundedMutation.mutate({ refundMethod: data.method, notes: data.notes })}
            isLoading={markRefundedMutation.isPending}
          />

          <CancelCreditModal
            isOpen={showCancelModal}
            onClose={() => setShowCancelModal(false)}
            credit={{
              id: credit.id,
              customer_id: credit.customer.id,
              credit_amount: credit.credit_amount,
              remaining_amount: credit.remaining_amount,
              source_type: credit.source_type,
              source_invoice_id: credit.source_invoice_id,
              description: credit.description,
              status: credit.status as 'issued' | 'applied' | 'refunded' | 'cancelled',
              credit_note_number: credit.credit_note_number,
              credit_note_issued_at: credit.credit_note_issued_at,
              credit_note_issued_by: credit.credit_note_issued_by,
              cancelled_at: credit.cancelled_at,
              cancelled_reason: credit.cancelled_reason,
              cancelled_by: null,
              created_at: credit.created_at,
              created_by: credit.created_by,
              updated_at: credit.created_at,
              source_invoice: credit.source_invoice,
            }}
            onCancel={(data) => cancelCreditMutation.mutate(data.reason)}
            isLoading={cancelCreditMutation.isPending}
          />
        </>
      )}
    </DetailPageLayout>
  );
};

export default CreditDetailsPage;
